import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Grid,
  Alert,
  Tabs,
  Tab,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  Link,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  AccountBalance,
  Business,
  CheckCircle,
  Warning,
  Info,
  Security,
  Api,
  Settings,
  Payment,
  Shield
} from '@mui/icons-material';

const OnlineBankingSetupDialog = ({ open, onClose, onSave, currentConfig, paymentMethod }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [config, setConfig] = useState({
    provider: 'fpx', // fpx, paynow_banking, duitnow, promptpay_banking
    enabled: true,
    testMode: true,
    fpx: {
      merchantId: '',
      merchantName: '',
      secretKey: '',
      encryptionKey: '',
      acquirerBankCode: '',
      selectedBanks: [],
      connected: false
    },
    paynowBanking: {
      merchantId: '',
      terminalId: '',
      apiKey: '',
      secretKey: '',
      connected: false
    },
    duitnow: {
      merchantCode: '',
      merchantName: '',
      creditorAccountNumber: '',
      creditorBankCode: '',
      connected: false
    },
    promptpayBanking: {
      merchantId: '',
      nationalId: '',
      mobileNumber: '',
      creditorAccountNumber: '',
      connected: false
    },
    integration: {
      webhookUrl: '',
      callbackUrl: '',
      environment: 'sandbox',
      timeoutMinutes: 15
    },
    displaySettings: {
      showBankSelection: true,
      bankSelectionType: 'redirect', // redirect, embedded
      instructions: '',
      timeout: 900 // 15 minutes
    },
    ...currentConfig
  });

  const bankingProviders = {
    fpx: {
      name: 'FPX Online Banking',
      description: 'Malaysia\'s national online banking network',
      countries: ['Malaysia'],
      documentation: 'https://www.mepsfpx.com.my/FPXMain/mdmDocumentDownload.jsp',
      requirements: [
        'FPX merchant account registration',
        'Bank Negara Malaysia approval',
        'API credentials from acquiring bank',
        'SSL certificate for secure transactions'
      ],
      supportedBanks: [
        { code: 'MBBEMYKL', name: 'Maybank', icon: '🏦' },
        { code: 'CIBBMYKL', name: 'CIMB Bank', icon: '🏦' },
        { code: 'PBBEMYKL', name: 'Public Bank', icon: '🏦' },
        { code: 'RHBBMYKL', name: 'RHB Bank', icon: '🏦' },
        { code: 'HLBBMYKL', name: 'Hong Leong Bank', icon: '🏦' },
        { code: 'AMBBMYKL', name: 'AmBank', icon: '🏦' },
        { code: 'UOVBMYKL', name: 'UOB Bank', icon: '🏦' },
        { code: 'OCBCMYKL', name: 'OCBC Bank', icon: '🏦' }
      ]
    },
    paynowBanking: {
      name: 'PayNow Online Banking',
      description: 'Singapore\'s instant payment service with banking integration',
      countries: ['Singapore'],
      documentation: 'https://www.abs.org.sg/industry-guidelines/paynow',
      requirements: [
        'Singapore business registration',
        'Participating bank account',
        'PayNow corporate registration',
        'API integration approval'
      ]
    },
    duitnow: {
      name: 'DuitNow Online Banking',
      description: 'Malaysia\'s real-time retail payment service',
      countries: ['Malaysia'],
      documentation: 'https://www.bnm.gov.my/duitnow',
      requirements: [
        'Malaysian business registration',
        'Participating bank account',
        'DuitNow merchant registration',
        'Real-time payment gateway setup'
      ]
    },
    promptpayBanking: {
      name: 'PromptPay Banking',
      description: 'Thailand\'s national e-payment system with banking',
      countries: ['Thailand'],
      documentation: 'https://www.bot.or.th/English/PaymentSystems/PolicyPS/Pages/PromptPay.aspx',
      requirements: [
        'Thai business registration',
        'Bank of Thailand compliance',
        'PromptPay merchant account',
        'Banking partner integration'
      ]
    }
  };

  useEffect(() => {
    if (currentConfig && Object.keys(currentConfig).length > 0) {
      setConfig(prevConfig => ({ ...prevConfig, ...currentConfig }));
    }
  }, [currentConfig]);

  const handleProviderChange = (providerId) => {
    setConfig(prev => ({ ...prev, provider: providerId }));
  };

  const handleConfigChange = (section, field, value) => {
    setConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleBankSelection = (bankCode, isSelected) => {
    setConfig(prev => ({
      ...prev,
      fpx: {
        ...prev.fpx,
        selectedBanks: isSelected 
          ? [...prev.fpx.selectedBanks, bankCode]
          : prev.fpx.selectedBanks.filter(code => code !== bankCode)
      }
    }));
  };

  const handleTestConnection = async (provider) => {
    // Simulate connection test
    setTimeout(() => {
      setConfig(prev => ({
        ...prev,
        [provider]: {
          ...prev[provider],
          connected: true
        }
      }));
    }, 2000);
  };

  const handleSave = () => {
    onSave({
      ...config,
      setupCompleted: config[config.provider].connected,
      lastUpdated: new Date().toISOString()
    });
  };

  const setupSteps = [
    t('posConfig.paymentMethods.setup.chooseProvider') || 'Choose Banking Provider',
    t('posConfig.paymentMethods.setup.businessRegistration') || 'Business Registration',
    t('posConfig.paymentMethods.setup.technicalSetup') || 'Technical Setup',
    t('posConfig.paymentMethods.setup.testingVerification') || 'Testing & Verification'
  ];

  const renderProviderConfig = () => {
    const provider = config.provider;
    const providerConfig = config[provider] || config[provider.replace('_', '')];
    
    switch (provider) {
      case 'fpx':
        return (
          <Box sx={{ display: 'grid', gap: 2 }}>
            <TextField
              fullWidth
              label={t('posConfig.paymentMethods.setup.merchantId') || 'Merchant ID'}
              value={providerConfig.merchantId}
              onChange={(e) => handleConfigChange('fpx', 'merchantId', e.target.value)}
              placeholder="FPX merchant identifier"
              helperText={t('posConfig.paymentMethods.setup.merchantIdHelper') || 'Your FPX-assigned merchant ID'}
            />
            
            <TextField
              fullWidth
              label={t('billing.fields.companyName') || 'Merchant Name'}
              value={providerConfig.merchantName}
              onChange={(e) => handleConfigChange('fpx', 'merchantName', e.target.value)}
              placeholder="Your business name"
              helperText={t('posConfig.paymentMethods.setup.merchantNameHelper') || 'Business name as registered with FPX'}
            />
            
            <TextField
              fullWidth
              label={t('posConfig.paymentMethods.setup.secretKey') || 'Secret Key'}
              type="password"
              value={providerConfig.secretKey}
              onChange={(e) => handleConfigChange('fpx', 'secretKey', e.target.value)}
              placeholder="FPX secret key"
              helperText={t('posConfig.paymentMethods.setup.secretKeyHelper') || 'Secret key for transaction signing'}
            />
            
            <TextField
              fullWidth
              label={t('posConfig.paymentMethods.setup.encryptionKey') || 'Encryption Key'}
              type="password"
              value={providerConfig.encryptionKey}
              onChange={(e) => handleConfigChange('fpx', 'encryptionKey', e.target.value)}
              placeholder="FPX encryption key"
              helperText={t('posConfig.paymentMethods.setup.encryptionKeyHelper') || 'Key for data encryption'}
            />
            
            <FormControl fullWidth>
              <InputLabel>{t('posConfig.paymentMethods.setup.acquirerBank') || 'Acquiring Bank'}</InputLabel>
              <Select
                value={providerConfig.acquirerBankCode}
                onChange={(e) => handleConfigChange('fpx', 'acquirerBankCode', e.target.value)}
                label={t('posConfig.paymentMethods.setup.acquirerBank') || 'Acquiring Bank'}
              >
                <MenuItem value="MBBEMYKL">Maybank</MenuItem>
                <MenuItem value="CIBBMYKL">CIMB Bank</MenuItem>
                <MenuItem value="PBBEMYKL">Public Bank</MenuItem>
                <MenuItem value="RHBBMYKL">RHB Bank</MenuItem>
                <MenuItem value="HLBBMYKL">Hong Leong Bank</MenuItem>
              </Select>
            </FormControl>

            {/* Bank Selection */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                {t('posConfig.paymentMethods.setup.supportedBanks') || 'Supported Banks'}
              </Typography>
              <Grid container spacing={1}>
                {bankingProviders.fpx.supportedBanks.map((bank) => (
                  <Grid item xs={6} sm={4} md={3} key={bank.code}>
                    <Card 
                      sx={{ 
                        cursor: 'pointer',
                        border: providerConfig.selectedBanks?.includes(bank.code) ? 2 : 1,
                        borderColor: providerConfig.selectedBanks?.includes(bank.code) 
                          ? 'primary.main' 
                          : 'grey.300',
                        '&:hover': { borderColor: 'primary.main' }
                      }}
                      onClick={() => handleBankSelection(bank.code, !providerConfig.selectedBanks?.includes(bank.code))}
                    >
                      <CardContent sx={{ p: 1.5, textAlign: 'center' }}>
                        <Typography variant="h6">{bank.icon}</Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                          {bank.name}
                        </Typography>
                        {providerConfig.selectedBanks?.includes(bank.code) && (
                          <CheckCircle color="primary" sx={{ fontSize: 16, mt: 0.5 }} />
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Button
              variant="contained"
              onClick={() => handleTestConnection('fpx')}
              disabled={!providerConfig.merchantId || !providerConfig.secretKey}
              startIcon={providerConfig.connected ? <CheckCircle /> : <AccountBalance />}
              color={providerConfig.connected ? 'success' : 'primary'}
            >
              {providerConfig.connected 
                ? (t('status.connected') || 'Connected')
                : (t('posConfig.paymentMethods.setup.testConnection') || 'Test Connection')
              }
            </Button>
          </Box>
        );
        
      case 'paynowBanking':
        return (
          <Box sx={{ display: 'grid', gap: 2 }}>
            <TextField
              fullWidth
              label={t('posConfig.paymentMethods.setup.merchantId') || 'Merchant ID'}
              value={providerConfig.merchantId}
              onChange={(e) => handleConfigChange('paynowBanking', 'merchantId', e.target.value)}
              placeholder="PayNow merchant ID"
            />
            
            <TextField
              fullWidth
              label={t('posConfig.paymentMethods.setup.terminalId') || 'Terminal ID'}
              value={providerConfig.terminalId}
              onChange={(e) => handleConfigChange('paynowBanking', 'terminalId', e.target.value)}
              placeholder="PayNow terminal ID"
            />
            
            <TextField
              fullWidth
              label={t('posConfig.paymentMethods.setup.apiKey') || 'API Key'}
              type="password"
              value={providerConfig.apiKey}
              onChange={(e) => handleConfigChange('paynowBanking', 'apiKey', e.target.value)}
              placeholder="PayNow API key"
            />
            
            <TextField
              fullWidth
              label={t('posConfig.paymentMethods.setup.secretKey') || 'Secret Key'}
              type="password"
              value={providerConfig.secretKey}
              onChange={(e) => handleConfigChange('paynowBanking', 'secretKey', e.target.value)}
              placeholder="PayNow secret key"
            />
          </Box>
        );
        
      case 'duitnow':
        return (
          <Box sx={{ display: 'grid', gap: 2 }}>
            <TextField
              fullWidth
              label={t('posConfig.paymentMethods.setup.merchantCode') || 'Merchant Code'}
              value={providerConfig.merchantCode}
              onChange={(e) => handleConfigChange('duitnow', 'merchantCode', e.target.value)}
              placeholder="DuitNow merchant code"
            />
            
            <TextField
              fullWidth
              label={t('billing.fields.companyName') || 'Merchant Name'}
              value={providerConfig.merchantName}
              onChange={(e) => handleConfigChange('duitnow', 'merchantName', e.target.value)}
              placeholder="Business name"
            />
            
            <TextField
              fullWidth
              label={t('posConfig.paymentMethods.setup.creditorAccount') || 'Creditor Account Number'}
              value={providerConfig.creditorAccountNumber}
              onChange={(e) => handleConfigChange('duitnow', 'creditorAccountNumber', e.target.value)}
              placeholder="Bank account number"
            />
            
            <FormControl fullWidth>
              <InputLabel>{t('posConfig.paymentMethods.setup.bankCode') || 'Bank Code'}</InputLabel>
              <Select
                value={providerConfig.creditorBankCode}
                onChange={(e) => handleConfigChange('duitnow', 'creditorBankCode', e.target.value)}
                label={t('posConfig.paymentMethods.setup.bankCode') || 'Bank Code'}
              >
                <MenuItem value="MBBEMYKL">Maybank</MenuItem>
                <MenuItem value="CIBBMYKL">CIMB Bank</MenuItem>
                <MenuItem value="PBBEMYKL">Public Bank</MenuItem>
                <MenuItem value="RHBBMYKL">RHB Bank</MenuItem>
              </Select>
            </FormControl>
          </Box>
        );
        
      default:
        return (
          <Alert severity="info">
            {t('posConfig.paymentMethods.setup.configFormComingSoon') || 'Configuration form for this provider will be available soon.'}
          </Alert>
        );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AccountBalance />
          <Typography variant="h6">
            {t('posConfig.paymentMethods.setup.onlineBankingTitle') || 'Online Banking Setup'}
          </Typography>
          {paymentMethod && (
            <Chip label={paymentMethod.name} color="primary" size="small" />
          )}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
          <Tab label={t('posConfig.paymentMethods.setup.provider') || 'Provider'} />
          <Tab label={t('posConfig.paymentMethods.setup.configuration') || 'Configuration'} />
          <Tab label={t('posConfig.paymentMethods.setup.integration') || 'Integration'} />
          <Tab label={t('posConfig.paymentMethods.setup.testing') || 'Testing'} />
        </Tabs>

        {/* Tab 1: Provider Selection */}
        {activeTab === 0 && (
          <Box>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t('posConfig.paymentMethods.setup.selectBankingProvider') || 'Select your online banking provider based on your business location and banking relationships.'}
            </Typography>

            <Grid container spacing={2}>
              {Object.entries(bankingProviders).map(([key, provider]) => (
                <Grid item xs={12} md={6} key={key}>
                  <Card 
                    sx={{ 
                      cursor: 'pointer',
                      border: config.provider === key ? 2 : 1,
                      borderColor: config.provider === key ? 'primary.main' : 'grey.300',
                      '&:hover': { borderColor: 'primary.main' }
                    }}
                    onClick={() => handleProviderChange(key)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                          <Typography variant="h6">{provider.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {provider.description}
                          </Typography>
                        </Box>
                        {config.provider === key && (
                          <CheckCircle color="primary" />
                        )}
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          {t('posConfig.paymentMethods.setup.supportedCountries') || 'Supported Countries'}:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                          {provider.countries.map((country) => (
                            <Chip key={country} label={country} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t('posConfig.paymentMethods.setup.requirements') || 'Requirements'}:
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          {provider.requirements.map((req, index) => (
                            <Typography key={index} variant="caption" sx={{ display: 'block', ml: 1 }}>
                              • {req}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Tab 2: Configuration */}
        {activeTab === 1 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {bankingProviders[config.provider]?.name} {t('posConfig.paymentMethods.setup.setup') || 'Setup'}
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={config.testMode}
                  onChange={(e) => setConfig(prev => ({ ...prev, testMode: e.target.checked }))}
                />
              }
              label={t('posConfig.paymentMethods.setup.testMode') || 'Test Mode (Use sandbox environment)'}
              sx={{ mb: 3 }}
            />

            {renderProviderConfig()}

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>{t('posConfig.paymentMethods.setup.setupRequirements') || 'Setup Requirements'}:</strong>
                <br />
                {bankingProviders[config.provider]?.requirements.map((req, index) => (
                  <span key={index}>• {req}<br /></span>
                ))}
              </Typography>
            </Alert>
          </Box>
        )}

        {/* Tab 3: Integration Settings */}
        {activeTab === 2 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('posConfig.paymentMethods.setup.integrationSettings') || 'Integration Settings'}
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>{t('posConfig.paymentMethods.setup.environment') || 'Environment'}</InputLabel>
                  <Select
                    value={config.integration.environment}
                    onChange={(e) => handleConfigChange('integration', 'environment', e.target.value)}
                    label={t('posConfig.paymentMethods.setup.environment') || 'Environment'}
                  >
                    <MenuItem value="sandbox">
                      {t('posConfig.paymentMethods.setup.sandbox') || 'Sandbox (Testing)'}
                    </MenuItem>
                    <MenuItem value="production">
                      {t('posConfig.paymentMethods.setup.production') || 'Production (Live)'}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('posConfig.paymentMethods.setup.webhookUrl') || 'Webhook URL'}
                  value={config.integration.webhookUrl}
                  onChange={(e) => handleConfigChange('integration', 'webhookUrl', e.target.value)}
                  placeholder="https://yourdomain.com/webhooks/banking"
                  helperText={t('posConfig.paymentMethods.setup.webhookUrlHelper') || 'URL to receive payment status notifications'}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('posConfig.paymentMethods.setup.returnUrl') || 'Return URL'}
                  value={config.integration.callbackUrl}
                  onChange={(e) => handleConfigChange('integration', 'callbackUrl', e.target.value)}
                  placeholder="https://yourdomain.com/payment/return"
                  helperText={t('posConfig.paymentMethods.setup.returnUrlHelper') || 'URL customers return to after banking'}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('posConfig.paymentMethods.setup.timeoutMinutes') || 'Payment Timeout (minutes)'}
                  type="number"
                  value={config.integration.timeoutMinutes}
                  onChange={(e) => handleConfigChange('integration', 'timeoutMinutes', parseInt(e.target.value))}
                  inputProps={{ min: 5, max: 60 }}
                  helperText={t('posConfig.paymentMethods.setup.timeoutHelper') || 'How long banking session remains active'}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                {t('posConfig.paymentMethods.setup.displaySettings') || 'Display Settings'}
              </Typography>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={config.displaySettings.showBankSelection}
                    onChange={(e) => handleConfigChange('displaySettings', 'showBankSelection', e.target.checked)}
                  />
                }
                label={t('posConfig.paymentMethods.setup.showBankSelection') || 'Show Bank Selection in POS'}
                sx={{ mb: 2 }}
              />
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>{t('posConfig.paymentMethods.setup.bankSelectionType') || 'Bank Selection Type'}</InputLabel>
                <Select
                  value={config.displaySettings.bankSelectionType}
                  onChange={(e) => handleConfigChange('displaySettings', 'bankSelectionType', e.target.value)}
                  label={t('posConfig.paymentMethods.setup.bankSelectionType') || 'Bank Selection Type'}
                >
                  <MenuItem value="redirect">
                    {t('posConfig.paymentMethods.setup.redirect') || 'Redirect to Bank'}
                  </MenuItem>
                  <MenuItem value="embedded">
                    {t('posConfig.paymentMethods.setup.embedded') || 'Embedded Selection'}
                  </MenuItem>
                </Select>
              </FormControl>
              
              <TextField
                fullWidth
                label={t('posConfig.paymentMethods.setup.customerInstructions') || 'Customer Instructions'}
                multiline
                rows={3}
                value={config.displaySettings.instructions}
                onChange={(e) => handleConfigChange('displaySettings', 'instructions', e.target.value)}
                placeholder={t('posConfig.paymentMethods.setup.instructionsPlaceholder') || 'Instructions shown to customers...'}
              />
            </Box>
          </Box>
        )}

        {/* Tab 4: Testing */}
        {activeTab === 3 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('posConfig.paymentMethods.setup.testIntegration') || 'Test Your Online Banking Integration'}
            </Typography>
            
            <Stepper orientation="vertical">
              {setupSteps.map((step, index) => (
                <Step key={step} active={index <= activeTab}>
                  <StepLabel>{step}</StepLabel>
                  <StepContent>
                    <Typography variant="body2" color="text.secondary">
                      {index === 0 && (t('posConfig.paymentMethods.setup.stepProvider') || "Select and configure your banking provider")}
                      {index === 1 && (t('posConfig.paymentMethods.setup.stepRegistration') || "Verify business registration and bank relationships")}
                      {index === 2 && (t('posConfig.paymentMethods.setup.stepTechnical') || "Configure technical integration settings")}
                      {index === 3 && (t('posConfig.paymentMethods.setup.stepTesting') || "Test with sample banking transactions")}
                    </Typography>
                  </StepContent>
                </Step>
              ))}
            </Stepper>

            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                {t('posConfig.paymentMethods.setup.securityFeatures') || 'Security Features'}
              </Typography>
              
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Shield color="success" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={t('posConfig.paymentMethods.setup.sslEncryption') || 'SSL/TLS Encryption'}
                    secondary={t('posConfig.paymentMethods.setup.sslDescription') || 'All communications are encrypted using industry-standard protocols'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Security color="success" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={t('posConfig.paymentMethods.setup.tokenization') || 'Transaction Tokenization'}
                    secondary={t('posConfig.paymentMethods.setup.tokenDescription') || 'Sensitive data is tokenized and never stored locally'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Api color="success" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={t('posConfig.paymentMethods.setup.webhookSecurity') || 'Webhook Verification'}
                    secondary={t('posConfig.paymentMethods.setup.webhookDescription') || 'All webhook notifications are cryptographically verified'}
                  />
                </ListItem>
              </List>
            </Box>

            <Alert severity="warning" sx={{ mt: 3 }}>
              <Typography variant="body2">
                <strong>{t('posConfig.paymentMethods.setup.testingNotes') || 'Testing Notes'}:</strong>
                <br />• {t('posConfig.paymentMethods.setup.testNote1') || 'Use sandbox environment for initial testing'}
                <br />• {t('posConfig.paymentMethods.setup.testNote2') || 'Verify webhook endpoints are accessible'}
                <br />• {t('posConfig.paymentMethods.setup.testNote3') || 'Test with small amounts before going live'}
                <br />• {t('posConfig.paymentMethods.setup.testNote4') || 'Coordinate with your banking partner for testing procedures'}
              </Typography>
            </Alert>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>
          {t('common.cancel') || 'Cancel'}
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained"
          disabled={!config[config.provider]?.connected}
        >
          {t('common.saveChanges') || 'Save Configuration'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OnlineBankingSetupDialog;